/**
 * Éditeur visuel de template d'étiquette avec drag-and-drop
 */

import { useState, useCallback } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { FieldConfig, ALL_AVAILABLE_FIELDS } from '../../types/label-templates.types';
import { LabelCanvas } from './LabelCanvas';
import { FieldPalette } from './FieldPalette';
import { FieldPropertiesPanel } from './FieldPropertiesPanel';

interface Props {
  widthMm: number;
  heightMm: number;
  dpi: number;
  fields: FieldConfig[];
  onChange: (fields: FieldConfig[]) => void;
}

export function VisualLabelEditor({
  widthMm,
  heightMm,
  dpi,
  fields,
  onChange,
}: Props) {
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [showGrid, setShowGrid] = useState(true);
  const [scale, setScale] = useState(1);
  const [history, setHistory] = useState<FieldConfig[][]>([fields]);
  const [historyIndex, setHistoryIndex] = useState(0);

  const selectedField = fields.find((f) => f.id === selectedFieldId) || null;

  // Sauvegarder dans l'historique
  const saveToHistory = useCallback((newFields: FieldConfig[]) => {
    setHistory((prev) => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(newFields);
      return newHistory.slice(-20); // Garder les 20 dernières actions
    });
    setHistoryIndex((prev) => Math.min(prev + 1, 19));
  }, [historyIndex]);

  // Mettre à jour les champs
  const updateFields = useCallback((newFields: FieldConfig[]) => {
    onChange(newFields);
    saveToHistory(newFields);
  }, [onChange, saveToHistory]);

  // Déplacer un champ
  const handleFieldMove = useCallback((fieldId: string, x: number, y: number) => {
    const newFields = fields.map((f) =>
      f.id === fieldId ? { ...f, x: Math.round(x * 2) / 2, y: Math.round(y * 2) / 2 } : f
    );
    onChange(newFields); // Pas de save history pour drag en temps réel
  }, [fields, onChange]);

  // Ajouter un nouveau champ
  const handleFieldAdd = useCallback((fieldId: string, x: number, y: number) => {
    const availableField = ALL_AVAILABLE_FIELDS.find((f) => f.id === fieldId);
    if (!availableField) return;

    // Pour les champs de texte libre, créer un ID unique
    let actualFieldId = fieldId;
    if (availableField.source === 'freetext') {
      // Générer un ID unique: freetext_1, freetext_2, etc.
      const existingFreetextFields = fields.filter((f) => f.id.startsWith('freetext_'));
      const maxNumber = existingFreetextFields.reduce((max, f) => {
        const match = f.id.match(/freetext_(\d+)/);
        return match ? Math.max(max, parseInt(match[1])) : max;
      }, 0);
      actualFieldId = `freetext_${maxNumber + 1}`;
    } else {
      // Pour les autres champs, vérifier s'ils existent déjà
      if (fields.find((f) => f.id === fieldId)) {
        alert('Ce champ est déjà présent sur le canvas');
        return;
      }
    }

    const maxZIndex = Math.max(0, ...fields.map((f) => f.zIndex));

    // Déterminer le suffixe automatique pour les champs de prix
    let defaultSuffix = '';
    if (availableField.type === 'currency') {
      if (fieldId === 'price_per_kg') {
        defaultSuffix = '€/kg';
      } else if (fieldId === 'price_per_unit') {
        defaultSuffix = '€/pce';
      } else {
        defaultSuffix = '€';
      }
    }

    const newField: FieldConfig = {
      id: actualFieldId,
      source: availableField.source || 'product',
      field: availableField.field,
      enabled: true,
      label: availableField.label,
      x: Math.round(x * 2) / 2,
      y: Math.round(y * 2) / 2,
      fontSize: 10,
      fontWeight: 'normal',
      align: 'left',
      type: availableField.type,
      suffix: defaultSuffix || undefined,
      zIndex: maxZIndex + 1,
      freeText: availableField.source === 'freetext' ? 'Texte à personnaliser' : undefined,
    };

    const newFields = [...fields, newField];
    updateFields(newFields);
    setSelectedFieldId(fieldId);
  }, [fields, updateFields]);

  // Mettre à jour un champ
  const handleFieldUpdate = useCallback((updates: Partial<FieldConfig>) => {
    if (!selectedFieldId) return;

    const newFields = fields.map((f) =>
      f.id === selectedFieldId ? { ...f, ...updates } : f
    );
    updateFields(newFields);
  }, [selectedFieldId, fields, updateFields]);

  // Supprimer un champ
  const handleFieldDelete = useCallback(() => {
    if (!selectedFieldId) return;

    if (!confirm('Voulez-vous vraiment supprimer ce champ ?')) return;

    const newFields = fields.filter((f) => f.id !== selectedFieldId);
    updateFields(newFields);
    setSelectedFieldId(null);
  }, [selectedFieldId, fields, updateFields]);

  // Annuler
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      onChange(history[historyIndex - 1]);
    }
  }, [historyIndex, history, onChange]);

  // Refaire
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      onChange(history[historyIndex + 1]);
    }
  }, [historyIndex, history, onChange]);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex flex-col h-full">
        {/* Toolbar */}
        <div className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Grille */}
              <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showGrid}
                  onChange={(e) => setShowGrid(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span>Grille</span>
              </label>

              {/* Zoom */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-700">Zoom:</span>
                <div className="flex gap-1">
                  {[0.5, 0.75, 1, 1.25, 1.5, 2].map((z) => (
                    <button
                      key={z}
                      type="button"
                      onClick={() => setScale(z)}
                      className={`px-2 py-1 text-xs rounded border ${
                        scale === z
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
                      }`}
                    >
                      {Math.round(z * 100)}%
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Undo/Redo */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleUndo}
                disabled={historyIndex === 0}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Annuler (Ctrl+Z)"
              >
                ↶ Annuler
              </button>
              <button
                type="button"
                onClick={handleRedo}
                disabled={historyIndex === history.length - 1}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Refaire (Ctrl+Y)"
              >
                ↷ Refaire
              </button>
            </div>
          </div>
        </div>

        {/* Layout principal */}
        <div className="flex-1 grid grid-cols-12 gap-4 p-4 overflow-hidden">
          {/* Palette des champs (gauche) */}
          <div className="col-span-2 overflow-y-auto">
            <FieldPalette activeFields={fields} />
          </div>

          {/* Canvas central */}
          <div className="col-span-6 overflow-auto">
            <LabelCanvas
              widthMm={widthMm}
              heightMm={heightMm}
              dpi={dpi}
              fields={fields}
              selectedFieldId={selectedFieldId}
              showGrid={showGrid}
              scale={scale}
              onFieldClick={setSelectedFieldId}
              onFieldMove={handleFieldMove}
              onFieldAdd={handleFieldAdd}
            />
          </div>

          {/* Panneau de propriétés (droite) */}
          <div className="col-span-4 overflow-y-auto">
            <FieldPropertiesPanel
              field={selectedField}
              onUpdate={handleFieldUpdate}
              onDelete={handleFieldDelete}
            />
          </div>
        </div>

        {/* Guide d'utilisation */}
        <div className="bg-blue-50 border-t border-blue-200 px-4 py-2">
          <div className="flex items-start gap-2 text-xs text-blue-800">
            <span className="font-semibold">💡 Guide:</span>
            <div className="flex-1">
              <span className="font-medium">1. Glissez-déposez</span> un champ de la palette vers le canvas •
              <span className="font-medium ml-2">2. Déplacez</span> le champ en le faisant glisser •
              <span className="font-medium ml-2">3. Cliquez</span> pour éditer les propriétés •
              <span className="font-medium ml-2">4. Ajustez</span> taille, position, police dans le panneau de droite •
              <span className="font-medium ml-2">5. Enregistrez</span> et testez l'aperçu depuis la page d'impression
            </div>
          </div>
        </div>
      </div>
    </DndProvider>
  );
}
